import { resolveSemanticColor } from '@/theme/semanticTokens';
import { type RadiusToken, type SpacingToken } from '@/theme/tokens';
import { r, s } from '@/theme/tokens';
import { ButtonBase } from './ButtonBase';
import { useTheme } from './hooks';
import type { ButtonBaseProps } from './ButtonBase';
import type { SxProps, Theme } from './types';
import type { ReactElement, ReactNode } from 'react';

const SPACING_MAP: Record<SpacingToken, string> = {
  none: s('none'),
  xxxs: s('xxxs'),
  xxs: s('xxs'),
  xs: s('xs'),
  sm: s('sm'),
  md: s('md'),
  lg: s('lg'),
  xl: s('xl'),
  '2xl': s('2xl'),
  '3xl': s('3xl'),
  '4xl': s('4xl'),
  '5xl': s('5xl'),
  gutter: s('gutter'),
  section: s('section'),
};

const RADIUS_MAP: Record<RadiusToken, string> = {
  none: r('none'),
  sm: r('sm'),
  md: r('md'),
  card: r('card'),
};

const resolveSpacing = (token: SpacingToken | null | undefined): string | undefined =>
  token ? SPACING_MAP[token] : undefined;

type SurfaceButtonProps = Omit<ButtonBaseProps, 'children' | 'sx'> & {
  children: ReactNode;
  padding?: SpacingToken | null;
  radius?: RadiusToken;
  border?: boolean;
  background?: 'surface' | 'transparent';
  sx?: SxProps<Theme>;
};

export const SurfaceButton = ({
  children,
  padding = 'md',
  radius = 'card',
  border = true,
  background = 'surface',
  sx,
  ...rest
}: SurfaceButtonProps): ReactElement => {
  const theme = useTheme();
  const resolvedBackground =
    background === 'transparent' ? 'transparent' : resolveSemanticColor(theme, 'surface');
  const baseSx: SxProps<Theme> = {
    width: '100%',
    minWidth: 0,
    display: 'block',
    padding: resolveSpacing(padding),
    textAlign: 'inherit',
    color: 'inherit',
    borderRadius: RADIUS_MAP[radius],
    border: border ? `1px solid ${resolveSemanticColor(theme, 'border')}` : 'none',
    boxSizing: 'border-box',
    backgroundColor: resolvedBackground,
    WebkitTapHighlightColor: 'transparent',
    overflow: 'hidden',
    '&:hover, &.Mui-focusVisible, &:active': {
      backgroundColor: resolvedBackground,
    },
  };
  const resolvedSx = sx ? [baseSx, ...(Array.isArray(sx) ? sx : [sx])] : baseSx;

  return (
    <ButtonBase sx={resolvedSx} {...rest}>
      {children}
    </ButtonBase>
  );
};
